import React from 'react';
import PropTypes from 'prop-types';

import { useContext } from '@hooks';
import { mergeClasses } from '@utils';

import { ProductCardContext } from './ProductCard';

/**
 * ProductCard.Ratings is a child component that displays the Product Ratings within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Ratings
 * @param {Object} props - The properties for the ProductCard.Ratings component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Ratings container.
 * @returns {ReactElement} The rendered ProductCard.Ratings component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Ratings />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardRatings({ className, ...restProps }) {
  const { productId } = useContext(
    ProductCardContext,
    'ProductCard.Ratings must be a child of ProductCard.'
  );

  return (
    <div
      className={mergeClasses(
        'flex min-h-[28px] items-center overflow-hidden px-2 @md:px-4',
        className
      )}
      {...restProps}
    >
      <div
        data-bv-show="inline_rating"
        data-bv-product-id={`${productId}`}
        data-bv-seo="false"
      />
    </div>
  );
}

ProductCardRatings.propTypes = {
  className: PropTypes.string
};

ProductCardRatings.defaultProps = {
  className: ''
};
